
import React from "react";
import { StaffMember } from "@/services/staffService";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import StaffFormFields from "../form/StaffFormFields";
import { useStaffForm } from "@/hooks/useStaffForm";

interface EditStaffDialogProps {
  staff: StaffMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

const EditStaffDialog: React.FC<EditStaffDialogProps> = ({
  staff,
  open,
  onOpenChange,
  onUpdate,
}) => {
  // We'll create this hook to handle staff form logic
  const {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleImageUpload,
    saveStaff
  } = useStaffForm({ 
    staff, 
    onUpdated: (updatedStaff) => {
      onUpdate();
      onOpenChange(false);
    }
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveStaff();
  };

  // Ensure we don't render anything if staff is null
  if (!staff) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-purple-50 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900">Edit Staff Member</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {`${staff.first_name} ${staff.last_name}`}
          </p>
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="edit-staff-form" onSubmit={handleFormSubmit}>
              <StaffFormFields
                formData={formData}
                handleChange={handleChange}
                handleSwitchChange={handleSwitchChange}
                handleImageUpload={handleImageUpload}
              />
            </form>
          </div>
          
          <div className="px-6 py-4 border-t bg-white/80 backdrop-blur-sm flex justify-end gap-3 flex-shrink-0 sticky bottom-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              form="edit-staff-form" 
              type="submit" 
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditStaffDialog;
